import React from 'react';
import { 
  Card, 
  CardMedia, 
  CardContent, 
  Typography, 
  Chip, 
  Box 
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { utils, Speaker } from '../../services/wordpressApi';
import { proxiedMediaUrl } from '../../services/mediaProxy';

interface SpeakerCardProps {
  speaker: Speaker;
  to?: string;
  onClick?: (speaker: Speaker) => void;
  showActions?: boolean;
  variant?: 'standard' | 'featured';
}

const SpeakerCard: React.FC<SpeakerCardProps> = ({ 
  speaker, 
  to,
  onClick,
  showActions = true,
  variant = 'standard'
}) => {
  // Use proxied media URLs so we don't expose the WordPress origin.
  const featuredImage = (() => {
    const sliderId = typeof (speaker as any).homepage_slider_image_id === 'number' ? (speaker as any).homepage_slider_image_id : 0;
    if (sliderId && sliderId > 0) return proxiedMediaUrl(sliderId, 'medium');
    if (speaker.featured_media && speaker.featured_media > 0) return proxiedMediaUrl(speaker.featured_media, 'medium');
    // Last resort (may expose origin):
    const rawUrl = speaker.featured_image_url || '';
    return rawUrl;
  })();
  const PlaceholderImage = 'https://business.wholelifechallenge.com/wp-content/uploads/2016/11/200x300.png';
  
  // Extract other data using existing utility functions
  const rawTopic: string | string[] = utils.getACFField(speaker, 'topic') || '';
  const topics: string[] = (() => {
    if (!rawTopic) return [];
    // Handle both array (new multi-select) and string (legacy comma-separated)
    const topicArray = Array.isArray(rawTopic) ? rawTopic : rawTopic.split(',');
    return topicArray
      .map((t) => utils.stripHTML(utils.decodeHTML(String(t))).trim())
      .filter(Boolean);
  })();
  const company = utils.getACFField(speaker, 'company');
  
  // Clean excerpt text and extract first sentence only
  const excerpt = (() => {
    const raw = speaker.excerpt?.rendered || '';
    // decode entities and strip HTML to get plain text only
    const decoded = utils.decodeHTML(raw);
    const noHtml = utils.stripHTML(decoded);
    // remove common formatting chars like ellipses brackets leftover
    const cleaned = noHtml
      .replace(/\s*\[\s*(?:…|&hellip;)\s*\]\s*$/i, '')
      .replace(/[•\u2022]/g, '-')
      .trim();
    
    // Remove ravespeakers.com domain
    const cleanedSource = utils.cleanSourceDomain(cleaned);

    // Extract first sentence only
    const sentences = cleanedSource.split(/[.!?]+/);
    const firstSentence = sentences[0]?.trim();
    return firstSentence ? firstSentence + '.' : cleanedSource;
  })();
  const speakerName = utils.stripHTML(speaker.title?.rendered || 'Unknown Speaker');

  const handleClick = (): void => {
    if (to) return; // navigation handled by RouterLink
    if (onClick) {
      onClick(speaker);
    }
  };

  const isFeatured = variant === 'featured';
  
  return (
    <Card 
      component={to ? (RouterLink as any) : 'div'}
      {...(to ? { to } : {})}
      sx={{ 
        height: '100%', 
        display: 'flex',
        flexDirection: isFeatured ? { xs: 'column', sm: 'row' } : 'column',
        cursor: to || onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': (to || onClick) ? {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        } : {},
        minHeight: isFeatured ? { sm: '250px' } : 'auto',
        bgcolor: '#222222',
        borderRadius: 4, // double rounding (was default)
        overflow: 'hidden',
        textDecoration: 'none',
      }}
      onClick={handleClick}
    >
      <CardMedia
        component="img"
        image={featuredImage || PlaceholderImage}
        alt={speakerName}
        sx={{
          objectFit: 'cover',
          objectPosition: '50% 25%', // shift focal point upward ~25%
          width: isFeatured ? { xs: '100%', sm: '40%' } : '100%',
          height: isFeatured ? { xs: 250, sm: '100%' } : 300,
          flexShrink: 0,
        }}
        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
          const target = e.target as HTMLImageElement;
          const rawUrl = speaker.featured_image_url;
          
          // If we are currently trying the proxy (or something else) and it failed
          // Try the raw URL if we haven't already
          if (rawUrl && target.src !== rawUrl && target.src !== PlaceholderImage) {
             target.src = rawUrl;
          } else if (target.src !== PlaceholderImage) {
             target.src = PlaceholderImage;
          }
        }}
      />
      
      <CardContent sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        width: isFeatured ? { xs: '100%', sm: '60%' } : '100%',
        p: isFeatured ? { xs: 2, sm: 3 } : 2,
      }}>
        <Typography 
          variant="h6"
          component="h3" 
          gutterBottom
          sx={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 'var(--weight-bold)',
            color: 'white',
            mb: 1,
            lineHeight: 1.0,
          }}
        >
          {speakerName}
        </Typography>
        
        {company && (
          <Typography 
            variant= "subtitle1"
            gutterBottom
            sx={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 'var(--weight-bold)',
              color: 'primary.main',
              mb: 1,
            }}
          >
            {company}
          </Typography>
        )}
        
        {topics.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {topics.map((topic, idx) => (
              <Chip 
                key={`${topic}-${idx}`}
                label={topic} 
                size={isFeatured ? "medium" : "small"}
                variant="outlined"
                sx={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 'var(--weight-regular)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                }}
              />
            ))}
          </Box>
        )}
        
        {excerpt && (
          <Typography 
            variant={isFeatured ? "body1" : "body2"}
            sx={{
              fontFamily: 'var(--font-body)',
              lineHeight: 1.5,
              flexGrow: 1,
              color: 'rgba(255, 255, 255, 0.8)',
            }}
          >
            {excerpt}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default SpeakerCard;